import { GetApp as ExportIcon } from "@mui/icons-material";
import {
  alpha,
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { Toolbar } from "common";
import useSettings from "features/settings/useSettings";
import { formatNumber, formatPrice } from "helpers/priceHelper";
import { usePivotReportContext } from "../usePivotReport";

export interface PivotHeatMapProps {}

// Helper function for color interpolation
const interpolateColor = (
  colorStops: Array<{ position: number; color: string }>,
  value: number,
): string => {
  // Rendezzük position szerint
  const sortedStops = [...colorStops].sort((a, b) => a.position - b.position);

  // Ha értékünk a legkisebb position alatt van
  if (value <= sortedStops[0].position) {
    return sortedStops[0].color;
  }

  // Ha értékünk a legnagyobb position felett van
  if (value >= sortedStops[sortedStops.length - 1].position) {
    return sortedStops[sortedStops.length - 1].color;
  }

  // Keresünk két stop között
  for (let i = 0; i < sortedStops.length - 1; i++) {
    const start = sortedStops[i];
    const end = sortedStops[i + 1];

    if (value >= start.position && value <= end.position) {
      // Interpoláció a két szín között
      const ratio = (value - start.position) / (end.position - start.position);
      return lerpColor(start.color, end.color, ratio);
    }
  }

  return sortedStops[0].color;
};

// Linear interpolation between two hex colors
const lerpColor = (color1: string, color2: string, ratio: number): string => {
  const hex1 = color1.replace("#", "");
  const hex2 = color2.replace("#", "");

  const r1 = parseInt(hex1.substr(0, 2), 16);
  const g1 = parseInt(hex1.substr(2, 2), 16);
  const b1 = parseInt(hex1.substr(4, 2), 16);

  const r2 = parseInt(hex2.substr(0, 2), 16);
  const g2 = parseInt(hex2.substr(2, 2), 16);
  const b2 = parseInt(hex2.substr(4, 2), 16);

  const r = Math.round(r1 + (r2 - r1) * ratio);
  const g = Math.round(g1 + (g2 - g1) * ratio);
  const b = Math.round(b1 + (b2 - b1) * ratio);

  return `#${r.toString(16).padStart(2, "0")}${g
    .toString(16)
    .padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
};

const PivotHeatMap: React.FC<PivotHeatMapProps> = ({}) => {
  const { data, params, setParams, heatMapColors, excelUrl } =
    usePivotReportContext();
  const [settings, setSettings] = useSettings({ key: "pivotReport" });
  const theme = useTheme();

  if (!data || !data.pivot_matrix || !data.pivot_matrix.matrix) {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography variant="body1" color="textSecondary">
          Nincs megjeleníthető adat
        </Typography>
      </Paper>
    );
  }

  const {
    matrix,
    row_labels,
    column_labels,
    heat_map_config,
    secondary_heat_map_config,
    meta,
  } = data.pivot_matrix;

  // Calculate min/max values for heat-map coloring (csak valid értékekkel)
  const allValues: number[] = [];
  Object.keys(matrix).forEach(rowKey => {
    Object.keys(matrix[rowKey]).forEach(colKey => {
      const cellData = matrix[rowKey][colKey];
      if (cellData && cellData.is_valid) {
        allValues.push(cellData.value);
      }
    });
  });

  const maxValue = allValues.length > 0 ? Math.max(...allValues) : 0;
  const minValue = allValues.length > 0 ? Math.min(...allValues) : 0;
  const formatValue = (
    value: number,
    valueKey: "primaryMeasure" | "secondaryMeasure" = "primaryMeasure",
  ) => {
    const measureName =
      valueKey === "primaryMeasure"
        ? params.primaryMeasure
        : params.secondaryMeasure;
    const measureConfig = data?.config?.available_measures?.[measureName || ""];

    if (!measureConfig) {
      return formatNumber(value);
    }

    switch (measureConfig.format) {
      case "currency":
        return formatPrice(value, settings.currency, settings.short_prices);
      case "percentage":
        return `${value.toFixed(1)}%`;
      case "number":
      default:
        return formatNumber(value, measureConfig.type === "avg" ? 2 : 0);
    }
  };

  const getCellColor = (value: number) => {
    if (!params.heatMapEnabled || !heat_map_config) return "transparent";

    // SIMPLE & PROFESSIONAL: Single color + opacity (like GitHub, Google Analytics)
    if (heat_map_config.type === "single_color" || !heat_map_config.type) {
      const { min_value = 0, max_value = 100, base_color } = heat_map_config;

      // Normalizálás 0-1 közé
      const normalizedValue =
        max_value > min_value
          ? Math.max(
              0,
              Math.min(1, (value - min_value) / (max_value - min_value)),
            )
          : 0;

      // Intelligens opacity: alacsony értékeknél is látható, de nem túl erős
      const minOpacity = 0.08; // Alig látható minimum
      const maxOpacity = theme.palette.mode === "dark" ? 0.8 : 0.9; // Dark mode-ban kevésbé erős
      const opacity = minOpacity + normalizedValue * (maxOpacity - minOpacity);

      // Theme-aware base color
      const themeColor =
        base_color || getThemeColorForMeasure(params.primaryMeasure || "");
      return alpha(themeColor, opacity);
    }

    // Legacy: continuous gradients (complex, but keep for backward compatibility)
    if (heat_map_config.type === "continuous") {
      const {
        min_value = 0,
        max_value = 100,
        gradient_type = "opacity",
        base_color,
        color_stops,
      } = heat_map_config;

      const normalizedValue =
        max_value > min_value
          ? Math.max(
              0,
              Math.min(1, (value - min_value) / (max_value - min_value)),
            )
          : 0;

      switch (gradient_type) {
        case "opacity":
          const opacity = Math.max(0.05, normalizedValue);
          const themeColor =
            base_color || getThemeColorForMeasure(params.primaryMeasure || "");
          return alpha(themeColor, opacity);
        case "gradient":
          if (color_stops && color_stops.length >= 2) {
            return interpolateColor(color_stops, normalizedValue);
          }
          break;
      }
    }

    // Fallback: simple theme-based
    return getDefaultThemeColor(value, params.primaryMeasure || "");
  };

  // Theme alapú színek mérőszámok szerint
  const getThemeColorForMeasure = (measure: string): string => {
    const measureConfig = data?.config?.available_measures?.[measure];

    if (!measureConfig) {
      return theme.palette.primary.main;
    }

    // Use server-provided colors if available
    if (measureConfig.colors) {
      return theme.palette.mode === "dark"
        ? measureConfig.colors.dark
        : measureConfig.colors.base;
    }

    // Fallback: Color based on format type
    switch (measureConfig.format) {
      case "percentage":
        return theme.palette.warning.main; // Narancs/Sárga for percentages
      case "currency":
        return theme.palette.success.main; // Zöld for money
      case "number":
      default:
        return theme.palette.primary.main; // Primary szín (kék) for counts/numbers
    }
  };

  // Default theme alapú színezés
  const getDefaultThemeColor = (value: number, measure: string): string => {
    const baseColor = getThemeColorForMeasure(measure);
    // Egyszerű alpha változtatás az értékek alapján
    const opacity = Math.max(0.1, Math.min(1, value / 100));
    return alpha(baseColor, opacity);
  };

  // Secondary heatmap color calculation for subtle indicator
  const getSecondaryIndicatorColor = (value: number) => {
    if (
      !params.heatMapEnabled ||
      !params.secondaryMeasure ||
      !secondary_heat_map_config
    )
      return "transparent";

    // SUBTLE INDICATOR: Lower opacity and smaller range
    if (
      secondary_heat_map_config.type === "single_color" ||
      !secondary_heat_map_config.type
    ) {
      const {
        min_value = 0,
        max_value = 100,
        base_color,
      } = secondary_heat_map_config;

      // Normalizálás 0-1 közé
      const normalizedValue =
        max_value > min_value
          ? Math.max(
              0,
              Math.min(1, (value - min_value) / (max_value - min_value)),
            )
          : 0;

      // Much more subtle opacity for secondary indicator
      const minOpacity = 0.3;
      const maxOpacity = 0.7;
      const opacity = minOpacity + normalizedValue * (maxOpacity - minOpacity);

      // Use secondary measure theme color or config color
      const themeColor =
        base_color || getThemeColorForMeasure(params.secondaryMeasure || "");
      return alpha(themeColor, opacity);
    }

    // Fallback for other types
    const fallbackColor = getThemeColorForMeasure(
      params.secondaryMeasure || "",
    );
    return alpha(fallbackColor, 0.5);
  };

  const getCellTextColor = (
    backgroundColor: string,
    value: number,
    normalizedValue?: number,
  ) => {
    // Smart contrast calculation based on opacity and theme

    // Calculate actual brightness of the background
    const rgb = backgroundColor.match(/\d+/g);
    if (!rgb || rgb.length < 3) {
      return theme.palette.text.primary;
    }

    const [r, g, b] = rgb.map(Number);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;

    // Get opacity from rgba string or calculate from normalized value
    let opacity = 1;
    if (backgroundColor.includes("rgba")) {
      const alphaMatch = backgroundColor.match(/[\d.]+\)$/);
      if (alphaMatch) {
        opacity = parseFloat(alphaMatch[0].replace(")", ""));
      }
    } else if (normalizedValue !== undefined) {
      const minOpacity = 0.08;
      const maxOpacity = theme.palette.mode === "dark" ? 0.8 : 0.9;
      opacity = minOpacity + normalizedValue * (maxOpacity - minOpacity);
    }

    // Effective brightness considering opacity and theme background
    const bgBrightness = theme.palette.mode === "dark" ? 30 : 255; // Approximate bg brightness
    const effectiveBrightness =
      bgBrightness + (brightness - bgBrightness) * opacity;

    // High opacity on dark backgrounds = use white text
    // High opacity on light backgrounds = use black text
    // Low opacity anywhere = use theme default
    if (opacity > 0.6) {
      return effectiveBrightness > 128
        ? theme.palette.text.primary
        : theme.palette.getContrastText(`rgb(${r}, ${g}, ${b})`);
    }

    // For low opacity, always use theme default (it's always readable)
    return theme.palette.text.primary;
  };

  // Calculate totals for display (csak valid értékekkel)
  // FIGYELEM: Egyes mérőszámoknál (utilization, avg_price) átlagot kell számolni, nem összeget!
  const shouldCalculateAverage = (measure: string): boolean => {
    const measureConfig = data?.config?.available_measures?.[measure];
    return measureConfig?.type === "avg";
  };

  const calculateRowTotals = () => {
    const rowTotals: {
      [key: string]: { primary: number; secondary?: number };
    } = {};
    const isPrimaryAverage = shouldCalculateAverage(
      params.primaryMeasure || "",
    );
    const isSecondaryAverage = params.secondaryMeasure
      ? shouldCalculateAverage(params.secondaryMeasure)
      : false;

    row_labels.forEach(rowLabel => {
      let primaryTotal = 0;
      let secondaryTotal = 0;
      let primaryCount = 0;
      let secondaryCount = 0;

      column_labels.forEach(colLabel => {
        const cellData = matrix[rowLabel]?.[colLabel];
        if (cellData && cellData.is_valid) {
          primaryTotal += cellData.value;
          primaryCount++;

          if (
            params.secondaryMeasure &&
            cellData.secondary_value !== null &&
            cellData.secondary_value !== undefined
          ) {
            secondaryTotal += cellData.secondary_value;
            secondaryCount++;
          }
        }
      });

      // Átlag vs összeg számítás
      rowTotals[rowLabel] = {
        primary:
          isPrimaryAverage && primaryCount > 0
            ? primaryTotal / primaryCount
            : primaryTotal,
        secondary:
          params.secondaryMeasure && secondaryCount > 0
            ? isSecondaryAverage
              ? secondaryTotal / secondaryCount
              : secondaryTotal
            : undefined,
      };
    });
    return rowTotals;
  };

  const calculateColumnTotals = () => {
    const colTotals: {
      [key: string]: { primary: number; secondary?: number };
    } = {};
    const isPrimaryAverage = shouldCalculateAverage(
      params.primaryMeasure || "",
    );
    const isSecondaryAverage = params.secondaryMeasure
      ? shouldCalculateAverage(params.secondaryMeasure)
      : false;

    column_labels.forEach(colLabel => {
      let primaryTotal = 0;
      let secondaryTotal = 0;
      let primaryCount = 0;
      let secondaryCount = 0;

      row_labels.forEach(rowLabel => {
        const cellData = matrix[rowLabel]?.[colLabel];
        if (cellData && cellData.is_valid) {
          primaryTotal += cellData.value;
          primaryCount++;

          if (
            params.secondaryMeasure &&
            cellData.secondary_value !== null &&
            cellData.secondary_value !== undefined
          ) {
            secondaryTotal += cellData.secondary_value;
            secondaryCount++;
          }
        }
      });

      // Átlag vs összeg számítás
      colTotals[colLabel] = {
        primary:
          isPrimaryAverage && primaryCount > 0
            ? primaryTotal / primaryCount
            : primaryTotal,
        secondary:
          params.secondaryMeasure && secondaryCount > 0
            ? isSecondaryAverage
              ? secondaryTotal / secondaryCount
              : secondaryTotal
            : undefined,
      };
    });
    return colTotals;
  };

  const rowTotals = calculateRowTotals();
  const columnTotals = calculateColumnTotals();
  console.log({ secondaryMeasure: params.secondaryMeasure });
  // Grand Total intelligens számítás
  const grandTotal = {
    primary: shouldCalculateAverage(params.primaryMeasure || "")
      ? Object.values(rowTotals).reduce((sum, val) => sum + val.primary, 0) /
        Object.values(rowTotals).length
      : Object.values(rowTotals).reduce((sum, val) => sum + val.primary, 0),
    secondary: params.secondaryMeasure
      ? shouldCalculateAverage(params.secondaryMeasure)
        ? Object.values(rowTotals)
            .filter(val => val.secondary !== undefined)
            .reduce((sum, val) => sum + (val.secondary || 0), 0) /
          Object.values(rowTotals).filter(val => val.secondary !== undefined)
            .length
        : Object.values(rowTotals).reduce(
            (sum, val) => sum + (val.secondary || 0),
            0,
          )
      : undefined,
  };

  // Mindig megjelenítjük a totalt (most már helyesen számoljuk)
  const shouldShowTotals = params.showTotals;

  return (
    <>
      <TableContainer
        component={Paper}
        sx={{
          //maxHeight: "70vh",
          overflow: "auto",
          overflowY: "visible",
          "& .MuiTable-root": {
            borderCollapse: "separate",
            borderSpacing: 0,
          },
        }}
      >
        <Table
          stickyHeader
          padding="none"
          sx={{
            "& td, th": {
              py: "6px",
              px: "12px",
              border: `1px solid ${theme.palette.divider}`,
              minWidth: "80px",
              textAlign: "center",
            },
            "& thead th": {
              backgroundColor: theme.palette.background.default,
              color: theme.palette.text.primary,
              fontWeight: "bold",
              position: "sticky",
              top: 0,
              zIndex: 2,
              borderBottom: `2px solid ${theme.palette.divider}`,
            },
            "& tbody td:first-of-type": {
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
              color: theme.palette.text.primary,
              fontWeight: "bold",
              position: "sticky",
              left: 0,
              zIndex: 1,
              borderRight: `2px solid ${theme.palette.divider}`,
            },
            "& .data-cell": {
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": {
                border: `1px solid ${theme.palette.primary.main}`,
                zIndex: 3,
              },
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell>
                {params.rowDimension === "time" && "Időpont"}
                {params.rowDimension === "date" && "Dátum"}
                {params.rowDimension === "month" && "Hónap"}
                {params.rowDimension === "weekday_hu" && "Hét napja"}
                {params.rowDimension === "program" && "Program"}
                {params.rowDimension === "boat" && "Hajó"}
                {params.rowDimension === "partner_group" && "Partner Csoport"}
                {params.rowDimension === "partner" && "Partner"}
              </TableCell>
              {column_labels.map((colHeader: string) => (
                <TableCell key={colHeader}>{colHeader}</TableCell>
              ))}
              {shouldShowTotals && (
                <TableCell>
                  {shouldCalculateAverage(params.primaryMeasure || "")
                    ? "Összesen (átlagértékekkel)"
                    : "Összesen"}
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {row_labels.map((rowHeader: string) => (
              <TableRow key={rowHeader}>
                <TableCell>{rowHeader}</TableCell>
                {column_labels.map((colHeader: string) => {
                  const cellData = matrix[rowHeader]?.[colHeader];

                  // Ha nincs adat vagy nem valid, akkor N/A kezelés
                  if (!cellData || !cellData.is_valid) {
                    return (
                      <TableCell
                        key={`${rowHeader}-${colHeader}`}
                        className="data-cell"
                        sx={{
                          backgroundColor: alpha(theme.palette.grey[500], 0.1),
                          color: theme.palette.text.disabled,
                          fontStyle: "italic",
                          position: "relative",
                        }}
                      >
                        <Tooltip
                          title={
                            <Box>
                              <Typography variant="body2">
                                <strong>
                                  {rowHeader} × {colHeader}
                                </strong>
                              </Typography>
                              <Typography variant="body2">
                                Nem értelmezhető adat
                              </Typography>
                            </Box>
                          }
                          arrow
                          placement="top"
                        >
                          <span>—</span>
                        </Tooltip>
                      </TableCell>
                    );
                  }

                  // Valid adat kezelése
                  const value = cellData.value;

                  // Calculate normalized value for better contrast calculation
                  const { min_value = 0, max_value = 100 } = heat_map_config;
                  const normalizedValue =
                    max_value > min_value
                      ? Math.max(
                          0,
                          Math.min(
                            1,
                            (value - min_value) / (max_value - min_value),
                          ),
                        )
                      : 0;

                  const backgroundColor = getCellColor(value);
                  const textColor = getCellTextColor(
                    backgroundColor,
                    value,
                    normalizedValue,
                  );

                  // Calculate secondary indicator color - only if secondary measure is selected
                  const secondaryColor =
                    params.secondaryMeasure &&
                    cellData.secondary_value !== null &&
                    cellData.secondary_value !== undefined
                      ? getSecondaryIndicatorColor(cellData.secondary_value)
                      : "transparent";

                  return (
                    <TableCell
                      key={`${rowHeader}-${colHeader}`}
                      className="data-cell"
                      sx={{
                        backgroundColor,
                        color: textColor,
                        position: "relative",
                        // Add subtle box-shadow as secondary indicator
                        boxShadow:
                          secondaryColor !== "transparent"
                            ? `inset 0 -3px 0 0 ${secondaryColor}`
                            : "none",
                      }}
                    >
                      <Tooltip
                        title={
                          <Box>
                            <Typography variant="body2">
                              <strong>
                                {rowHeader} × {colHeader}
                              </strong>
                            </Typography>
                            <Typography variant="body2">
                              {data?.config?.available_measures?.[
                                params.primaryMeasure || ""
                              ]?.label ?? "Elsődleges"}
                              : {formatValue(value)}
                            </Typography>
                            {params.secondaryMeasure &&
                              cellData.secondary_value !== null &&
                              cellData.secondary_value !== undefined && (
                                <Typography variant="body2">
                                  {data?.config?.available_measures?.[
                                    params.secondaryMeasure
                                  ]?.label ?? "Másodlagos"}
                                  :{" "}
                                  {formatValue(
                                    cellData.secondary_value,
                                    "secondaryMeasure",
                                  )}
                                </Typography>
                              )}
                          </Box>
                        }
                        arrow
                        placement="top"
                      >
                        <Box sx={{ textAlign: "center" }}>
                          <Typography variant="body2" component="div">
                            {formatValue(value)}
                          </Typography>
                          {params.secondaryMeasure &&
                            cellData.secondary_value !== null &&
                            cellData.secondary_value !== undefined && (
                              <Typography
                                variant="caption"
                                component="div"
                                sx={{
                                  fontSize: "0.65rem",
                                  opacity: 0.8,
                                  lineHeight: 1,
                                }}
                              >
                                {formatValue(
                                  cellData.secondary_value,
                                  "secondaryMeasure",
                                )}
                              </Typography>
                            )}
                        </Box>
                      </Tooltip>
                    </TableCell>
                  );
                })}
                {shouldShowTotals && (
                  <TableCell
                    sx={{
                      backgroundColor: alpha(
                        theme.palette.secondary.main,
                        0.12,
                      ),
                      color: theme.palette.text.primary,
                      fontWeight: "bold",
                      borderLeft: `2px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Box sx={{ textAlign: "center" }}>
                      <Typography variant="body2" component="div">
                        {formatValue(rowTotals[rowHeader]?.primary || 0)}
                      </Typography>
                      {params.secondaryMeasure &&
                        rowTotals[rowHeader]?.secondary !== undefined && (
                          <Typography
                            variant="caption"
                            component="div"
                            sx={{
                              fontSize: "0.65rem",
                              opacity: 0.8,
                              lineHeight: 1,
                            }}
                          >
                            {formatValue(
                              rowTotals[rowHeader]?.secondary || 0,
                              "secondaryMeasure",
                            )}
                          </Typography>
                        )}
                    </Box>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {shouldShowTotals && (
              <TableRow>
                <TableCell
                  sx={{
                    backgroundColor: alpha(theme.palette.secondary.main, 0.12),
                    color: theme.palette.text.primary,
                    fontWeight: "bold",
                    borderTop: `2px solid ${theme.palette.divider}`,
                  }}
                >
                  {shouldCalculateAverage(params.primaryMeasure || "")
                    ? "Összesen (átlagértékekkel)"
                    : "Összesen"}
                </TableCell>
                {column_labels.map((colHeader: string) => (
                  <TableCell
                    key={colHeader}
                    sx={{
                      backgroundColor: alpha(
                        theme.palette.secondary.main,
                        0.12,
                      ),
                      color: theme.palette.text.primary,
                      fontWeight: "bold",
                      borderTop: `2px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Box sx={{ textAlign: "center" }}>
                      <Typography variant="body2" component="div">
                        {formatValue(columnTotals[colHeader]?.primary || 0)}
                      </Typography>
                      {params.secondaryMeasure &&
                        columnTotals[colHeader]?.secondary !== undefined && (
                          <Typography
                            variant="caption"
                            component="div"
                            sx={{
                              fontSize: "0.65rem",
                              opacity: 0.8,
                              lineHeight: 1,
                            }}
                          >
                            {formatValue(
                              columnTotals[colHeader]?.secondary || 0,
                              "secondaryMeasure",
                            )}
                          </Typography>
                        )}
                    </Box>
                  </TableCell>
                ))}
                <TableCell
                  sx={{
                    backgroundColor: alpha(theme.palette.secondary.main, 0.2),
                    color: theme.palette.text.primary,
                    fontWeight: "bold",
                    borderTop: `2px solid ${theme.palette.divider}`,
                    borderLeft: `2px solid ${theme.palette.divider}`,
                  }}
                >
                  <Box sx={{ textAlign: "center" }}>
                    <Typography variant="body2" component="div">
                      {formatValue(grandTotal.primary)}
                    </Typography>
                    {params.secondaryMeasure &&
                      grandTotal.secondary !== null &&
                      grandTotal.secondary !== undefined && (
                        <Typography
                          variant="caption"
                          component="div"
                          sx={{
                            fontSize: "0.65rem",
                            opacity: 0.8,
                            lineHeight: 1,
                          }}
                        >
                          {formatValue(
                            grandTotal.secondary,
                            "secondaryMeasure",
                          )}
                        </Typography>
                      )}
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Toolbar
        sx={{
          mt: -1,
          mb: 2,
          justifyContent: "flex-end",
          "& label span.MuiTypography-root": {
            fontSize: "0.75rem",
          },
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              disabled={
                !params.primaryMeasure?.includes("amount") &&
                params.primaryMeasure !== "revenue"
              }
              checked={settings.short_prices}
              onChange={(evt: any) => {
                setSettings({ short_prices: !settings.short_prices });
              }}
              size="small"
            />
          }
          label="Rövid árak"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={params.heatMapEnabled}
              onChange={(evt: any) => {
                setParams({ heatMapEnabled: !params.heatMapEnabled });
              }}
              size="small"
            />
          }
          label="Heat-map színezés"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={params.showTotals}
              onChange={(evt: any) => {
                setParams({ showTotals: !params.showTotals });
              }}
              size="small"
            />
          }
          label={
            shouldCalculateAverage(params.primaryMeasure || "")
              ? "Összesítők mutatása (átlagértékekkel)"
              : "Összesítők mutatása"
          }
        />
        {excelUrl && (
          <Toolbar
            sx={{
              borderRadius: 1,
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: alpha(theme.palette.background.paper, 0.5),
            }}
          >
            <Tooltip title="Excel export">
              <IconButton
                component="a"
                href={excelUrl}
                target="_blank"
                size="small"
                sx={{ color: theme.palette.primary.main }}
              >
                <ExportIcon />
              </IconButton>
            </Tooltip>
          </Toolbar>
        )}
        {meta && (
          <Box
            sx={{
              display: "flex",
              gap: 2,
              fontSize: "0.75rem",
              color: theme.palette.text.secondary,
            }}
          >
            {meta.total_combinations && (
              <Typography variant="caption">
                Összes kombináció: {meta.total_combinations}
              </Typography>
            )}
            {meta.valid_combinations && (
              <Typography variant="caption">
                Valós adatok: {meta.valid_combinations}
              </Typography>
            )}
            {meta.empty_combinations && (
              <Typography variant="caption">
                Nem értelmezhető: {meta.empty_combinations}
              </Typography>
            )}
          </Box>
        )}
      </Toolbar>
    </>
  );
};

export default PivotHeatMap;
